/**
 * Theme per address (multi-business mode): the Invora palette, optionally with
 * a business's brand colour as the primary colour. Single-business mode applies
 * nothing, so the original palette in index.css stays.
 */

const BRAND_VARS = [
  "--primary",
  "--primary-foreground",
  "--ring",
  "--accent",
  "--accent-foreground",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--sidebar-accent",
  "--sidebar-accent-foreground",
  "--sidebar-ring",
  "--chart-1",
  "--scrollbar-thumb",
  "--scrollbar-thumb-hover",
];

/** Relative luminance of a #rrggbb colour (WCAG). */
function luminance(hex: string) {
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

export function applySiteTheme(options: { invora: boolean; brandColor?: string | null }) {
  const root = document.documentElement;
  if (options.invora) root.dataset.theme = "invora";
  else delete root.dataset.theme;
  for (const name of BRAND_VARS) root.style.removeProperty(name);

  const color = options.brandColor;
  if (!options.invora || !color || !/^#[0-9a-f]{6}$/i.test(color)) return;
  // Text on the brand colour: whichever of white / near-black reads better.
  const onColor = luminance(color) > 0.4 ? "oklch(0.2 0.02 270)" : "oklch(0.99 0 0)";
  const set = (name: string, value: string) => root.style.setProperty(name, value);
  set("--primary", color);
  set("--primary-foreground", onColor);
  set("--ring", `color-mix(in oklch, ${color} 70%, white)`);
  set("--accent", `color-mix(in oklch, ${color} 10%, white)`);
  set("--accent-foreground", `color-mix(in oklch, ${color} 80%, black)`);
  set("--sidebar-primary", color);
  set("--sidebar-primary-foreground", onColor);
  set("--sidebar-accent", `color-mix(in oklch, ${color} 10%, white)`);
  set("--sidebar-accent-foreground", `color-mix(in oklch, ${color} 80%, black)`);
  set("--sidebar-ring", `color-mix(in oklch, ${color} 70%, white)`);
  set("--chart-1", color);
  set("--scrollbar-thumb", `color-mix(in oklch, ${color} 32%, transparent)`);
  set("--scrollbar-thumb-hover", `color-mix(in oklch, ${color} 55%, transparent)`);
}
